import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calendar, Clock, ExternalLink, Plane, TrendingUp, Ship, Train, Globe, Truck } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { translations } from "@/utils/translations";

const Articles = () => {
  const { id } = useParams();
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const t = translations[currentLanguage as keyof typeof translations];
  
  const getArticleData = (articleId: string) => {
    const articleData = t.articles[articleId as keyof typeof t.articles];
    if (!articleData) return null;
    
    const iconMap: { [key: string]: any } = {
      "1": Plane,
      "2": TrendingUp,
      "3": Ship,
      "4": Train,
      "5": Globe,
      "6": Truck
    };
    
    const sourceUrlMap: { [key: string]: string } = {
      "1": "https://www.iata.org/en/iata-repository/publications/economic-reports/global-outlook-for-air-transport-december-2025",
      "2": "https://www.freightos.com/logistics-technology-insights/logistics-technology/what-logistics-procurement-managers-need-to-change-for-2026/",
      "3": "https://mykn.kuehne-nagel.com/news/article/tonnage-cascades-reshape-global-shipping-as",
      "4": "https://www.theguardian.com/business/2025/dec/30/eurostar-trains-cancelled-delayed-channel-tunnel-le-shuttle",
      "5": "https://www.joc.com/article/south-korea-to-invest-3-billion-expanding-foreign-logistics-terminal-network-6139878",
      "6": "https://www.sheingroup.com/corporate-news/company-updates/shein-opens-state-of-the-art-european-logistics-hub-in-wroclaw-poland-lifting-total-jobs-created-in-poland-to-5000/"
    };
    
    const imageMap: { [key: string]: string } = {
      "1": "https://images.unsplash.com/photo-1713846047266-12aa96cbbb6c?w=1200&auto=format&fit=crop&q=80",
      "2": "https://images.unsplash.com/photo-1532635042-a6f6ad4745f9?w=1200&auto=format&fit=crop&q=80",
      "3": "https://images.unsplash.com/photo-1628789665182-84e2b3757b59?w=1200&auto=format&fit=crop&q=80",
      "4": "https://images.unsplash.com/photo-1633365636129-0441d2b94d96?w=1200&auto=format&fit=crop&q=80",
      "5": "https://images.unsplash.com/photo-1759272548470-d0686d071036?w=1200&auto=format&fit=crop&q=80",
      "6": "https://images.unsplash.com/photo-1622030433415-153f4ada40fe?w=1200&auto=format&fit=crop&q=80"
    };
    
    return {
      title: articleData.title,
      category: articleData.category,
      icon: iconMap[articleId],
      date: articleData.date,
      readTime: articleData.readTime,
      image: imageMap[articleId],
      source: articleData.source,
      sourceUrl: sourceUrlMap[articleId],
      content: articleData.content
    };
  };
  
  const article = getArticleData(id || "");

  if (!article) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">{t.articleNotFound}</h1>
          <Link to="/">
            <Button>{t.returnHome}</Button>
          </Link>
        </div>
      </div>
    );
  }

  const IconComponent = article.icon;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-orange-500 rounded-lg flex items-center justify-center">
                <Globe className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">{t.portalName}</h1>
            </Link>
            <div className="flex items-center space-x-4">
              <LanguageSwitcher 
                currentLanguage={currentLanguage} 
                onLanguageChange={setCurrentLanguage} 
              />
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {t.backToHome}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Article Content */}
      <article className="py-8">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Article Header */}
          <div className="mb-8">
            <div className="mb-4">
              <Badge className="mb-4">
                <IconComponent className="w-3 h-3 mr-1" />
                {article.category}
              </Badge>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex items-center space-x-6 text-gray-600 mb-6">
              <div className="flex items-center">
                <Calendar className="w-4 h-4 mr-2" />
                {article.date}
              </div>
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-2" />
                {article.readTime}
              </div>
              <a 
                href={article.sourceUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center text-blue-600 hover:text-blue-700"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                {article.source}
              </a>
            </div>
          </div>

          {/* Featured Image */}
          <div className="mb-8">
            <img 
              src={article.image} 
              alt={article.title}
              className="w-full h-64 md:h-96 object-cover rounded-lg shadow-lg"
            />
          </div>

          {/* Article Content */}
          <div className="prose prose-lg max-w-none">
            {article.content.map((section: any, index: number) => (
              <div key={index} className="mb-8">
                {section.type === 'summary' && (
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg mb-8">
                    <p className="text-lg text-gray-700 leading-relaxed font-medium">
                      {section.text}
                    </p>
                  </div>
                )}
                
                {section.type === 'section' && (
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">
                      {section.title}
                    </h2>
                    <ul className="space-y-3">
                      {section.content.map((item: string, itemIndex: number) => (
                        <li key={itemIndex} className="flex items-start">
                          <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                          <span className="text-gray-700 leading-relaxed">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Source Attribution */}
          <div className="mt-12 p-6 bg-gray-50 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-2">{t.source}</h3>
            <p className="text-gray-600 mb-3">
              {t.sourceDescription} {article.source}. 
              {t.sourceNote}
            </p>
            <a 
              href={article.sourceUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
            >
              {t.readOriginal}
              <ExternalLink className="w-4 h-4 ml-2" />
            </a>
          </div>

          {/* Navigation */}
          <div className="mt-12 pt-8 border-t">
            <Link to="/">
              <Button className="bg-blue-600 hover:bg-blue-700">
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t.backToAllArticles}
              </Button>
            </Link>
          </div>
        </div>
      </article>
    </div>
  );
};

export default Articles;